import type React from 'react'
import type { OverlayTriggerState } from '@react-stately/overlays'
import { useRef, useCallback, useEffect } from 'react'
import { useHoverIntent } from './useHoverIntent'
import { getHeaderTracker } from '../tracking'

/** Grace period (ms) when moving between trigger and portaled popover content */
const HOVER_GRACE_MS = 150

interface UseAccountPopoverOptions {
  isSignedIn: boolean
}

/**
 * Encapsulates all hover-open / click-to-pin / dismiss / focus-suppression
 * behavior for the account popover.
 *
 * Returns refs and event handlers that the component wires into the JSX.
 */
export const useAccountPopover = ({ isSignedIn }: UseAccountPopoverOptions) => {
  const overlayStateRef = useRef<OverlayTriggerState | null>(null)
  const openedByHoverRef = useRef(false)
  const contentHoveredRef = useRef(false)
  const clickInsideContentRef = useRef(false)
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** When true, focus events inside the wrapper are blurred immediately.
   *  Armed before hover-open; cleared on pointer-down, close, pin, or keydown. */
  const suppressFocusRef = useRef(false)

  // ── helpers ──────────────────────────────────────────────────────────

  const cancelGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current)
      graceTimerRef.current = null
    }
  }, [])

  const resetHoverState = useCallback(() => {
    openedByHoverRef.current = false
    suppressFocusRef.current = false
  }, [])

  // ── grace-close ─────────────────────────────────────────────────────

  const scheduleGraceClose = useCallback(() => {
    cancelGraceTimer()
    graceTimerRef.current = setTimeout(() => {
      if (openedByHoverRef.current && !contentHoveredRef.current) {
        overlayStateRef.current?.close()
        resetHoverState()
      }
    }, HOVER_GRACE_MS)
  }, [cancelGraceTimer, resetHoverState])

  // ── hover intent on wrapper div ─────────────────────────────────────

  const accountRef = useHoverIntent<HTMLDivElement>({
    hoverIn: () => {
      cancelGraceTimer()
      if (!overlayStateRef.current?.isOpen) {
        suppressFocusRef.current = true
        overlayStateRef.current?.open()
        openedByHoverRef.current = true
      }
    },
    hoverOut: scheduleGraceClose,
  })

  // ── popover content hover ───────────────────────────────────────────

  const handleContentMouseEnter = useCallback(() => {
    contentHoveredRef.current = true
    cancelGraceTimer()
  }, [cancelGraceTimer])

  const handleContentMouseLeave = useCallback(() => {
    contentHoveredRef.current = false
    if (openedByHoverRef.current) {
      scheduleGraceClose()
    }
  }, [scheduleGraceClose])

  const handleContentMouseDown = useCallback(() => {
    clickInsideContentRef.current = true
  }, [])

  // ── focus suppression ───────────────────────────────────────────────

  /**
   * Timing-independent focus suppression for hover-open.
   *
   * SWAN (React Aria) schedules `.focus()` on the trigger button in a
   * `useEffect` after the popover state change renders.  It may fire
   * more than once (overlay mount focus + focus restoration), so we
   * keep the flag armed for the entire hover-open duration instead of
   * resetting on first capture.  The flag is cleared when the popover
   * closes, is pinned by click, or the user presses a key.
   */
  const handleTriggerFocusCapture = useCallback((e: React.FocusEvent) => {
    if (suppressFocusRef.current) {
      ;(e.target as HTMLElement)?.blur?.()
    }
  }, [])

  /** Clears the focus-suppression flag so that a user-initiated click
   *  does not blur the button (which would break SWAN's press tracking). */
  const handlePointerDownCapture = useCallback(() => {
    suppressFocusRef.current = false
  }, [])

  /** If the user presses any key while the popover is hover-open, they've
   *  switched to keyboard navigation — restore the focus ring. */
  useEffect(() => {
    const el = accountRef.current
    if (!el) return

    const restoreFocusRing = () => {
      suppressFocusRef.current = false
    }
    el.addEventListener('keydown', restoreFocusRing)
    return () => el.removeEventListener('keydown', restoreFocusRing)
  }, [])

  // ── cleanup ─────────────────────────────────────────────────────────

  useEffect(() => () => cancelGraceTimer(), [cancelGraceTimer])

  // ── click-outside dismiss ───────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      requestAnimationFrame(() => {
        if (clickInsideContentRef.current) {
          clickInsideContentRef.current = false
          return
        }

        if (!overlayStateRef.current?.isOpen) return

        const target = event.target as Node
        const insideTrigger = accountRef.current?.contains(target)

        if (!insideTrigger) {
          overlayStateRef.current.close()
          resetHoverState()
        }
      })
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [resetHoverState])

  // ── escape key dismiss ──────────────────────────────────────────────

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && overlayStateRef.current?.isOpen) {
        overlayStateRef.current.close()
        resetHoverState()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [resetHoverState])

  // ── click-to-pin ────────────────────────────────────────────────────

  /**
   * Click handler — runs AFTER SWAN's internal toggle().
   *
   * If the popover was hover-opened, SWAN just toggled it closed.
   * We re-open it to "pin" it via click (openedByHover → false).
   * If it was already click-opened, SWAN's toggle closes it naturally.
   */
  const handleAccountClick = useCallback(() => {
    getHeaderTracker().trackFlyOutView('Account Popover', isSignedIn ? 'signed-in' : 'signed-out')

    if (openedByHoverRef.current) {
      overlayStateRef.current?.open()
      resetHoverState()
    }
  }, [isSignedIn, resetHoverState])

  return {
    accountRef,
    overlayStateRef,
    handleTriggerFocusCapture,
    handlePointerDownCapture,
    handleContentMouseEnter,
    handleContentMouseLeave,
    handleContentMouseDown,
    handleAccountClick,
  }
}
